import { useCallback, useEffect, useRef } from 'react'

import axios, { type CancelToken } from 'axios'
import { getBefehlsnachricht } from '@/apis/Befehlsnachricht'
import { useBfnStore } from '@/store/useBfnStore'
import { Abruftoken } from '@/models/Abruftoken'
import TokenExpiredError from '@/features/befehlsnachrichten/errors/TokenExpiredError'
import { checkAbruftokenExpired } from '@/features/befehlsnachrichten/utils/abruftoken'
import { useShallow } from 'zustand/react/shallow'
import { useQueryClient } from '@tanstack/react-query'

/**
 * Fetches the Befehlsnachricht from backend and writes the returned Befehlsnachricht to the store.
 *
 * If the abruftoken is not yet part of the store it will be added.
 *
 * If the abruftoken is part of pendingTokens store, the abruftoken will be removed from it after
 * the Befehlsnachricht has been successfully addeed to befehlsnachrichten store.
 *
 * If `cancelToken` is set, don't forget that axios will reject the promise on cancellation.
 * See `axios.isCancel(error)` to catch this in error handling.
 */
export function useBefehlsnachrichtUpdater() {
  const [
    allBfnInStore,
    addDataToStore,
    updateBfnInStore,
    pendingTokens,
    removePendingToken
  ] = useBfnStore(
    useShallow((state) => [
      state.data,
      state.addData,
      state.updateBefehlsnachricht,
      state.pendingTokens,
      state.removePendingToken
    ])
  )
  // eslint-disable-next-line import-x/no-named-as-default-member
  const cancelTokenRef = useRef(axios.CancelToken.source())
  const queryClient = useQueryClient()
  const cancelRequest = useCallback(() => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Anfrage wurde abgebrochen')
      // eslint-disable-next-line import-x/no-named-as-default-member
      cancelTokenRef.current = axios.CancelToken.source()
    }
  }, [])

  const removePendingTokenIfExists = useCallback(
    (abruftoken: Abruftoken) => {
      if (
        pendingTokens.some(
          (pt) =>
            pt.typ === abruftoken.typ && pt.abruftoken === abruftoken.abruftoken
        )
      ) {
        removePendingToken(abruftoken)
      }
    },
    [pendingTokens, removePendingToken]
  )

  const updateBefehlsnachricht = useCallback(
    async (abruftoken: Abruftoken, cancelToken?: CancelToken) => {
      cancelRequest()

      if (checkAbruftokenExpired(abruftoken)) {
        removePendingTokenIfExists(abruftoken)
        throw new TokenExpiredError()
      }

      const befehlsnachricht = await queryClient.fetchQuery({
        queryKey: ['befehlsnachrichtUpdater', abruftoken],
        queryFn: () =>
          getBefehlsnachricht(
            abruftoken,
            cancelToken || cancelTokenRef.current.token
          ),
        staleTime: 0,
        gcTime: 0
      })

      if (Object.keys(allBfnInStore).includes(befehlsnachricht.id)) {
        updateBfnInStore(befehlsnachricht.id, befehlsnachricht)
      } else {
        addDataToStore(befehlsnachricht.id, abruftoken, befehlsnachricht)
      }

      removePendingTokenIfExists(abruftoken)

      return befehlsnachricht
    },
    [
      addDataToStore,
      allBfnInStore,
      cancelRequest,
      queryClient,
      removePendingTokenIfExists,
      updateBfnInStore
    ]
  )

  useEffect(() => {
    return () => {
      cancelRequest()
    }
  }, [cancelRequest])

  return updateBefehlsnachricht
}
